import React from "react";
import { formatScientific, formatCompactValue, formatImpact } from "../../../utils/format";

interface ImpactWithBaselineProps {
  projectAnnual: number;
  projectPerMwh: number | null;
  gridAnnual: number | null;
  gridPerMwh: number | null;
  metric: "dalys" | "pdfs" | "usd";
  colorClass?: string;
}

const METRIC_UNITS: Record<string, string> = {
  dalys: "DALYs",
  pdfs: "PDFs",
  usd: "USD",
};

/**
 * Compact impact display with per-MWh rate and grid baseline context.
 * Designed for table cells in project lists and dashboards.
 */
export const ImpactWithBaseline: React.FC<ImpactWithBaselineProps> = ({
  projectAnnual,
  projectPerMwh,
  gridAnnual,
  gridPerMwh,
  metric,
  colorClass,
}) => {
  const unit = METRIC_UNITS[metric];
  const isBurden = projectAnnual < 0;
  const defaultColor = isBurden ? "text-amber-400" : colorClass || "text-text-primary";

  const formattedValue =
    metric === "usd"
      ? formatImpact(Math.abs(projectAnnual), "usd")
      : `${formatCompactValue(Math.abs(projectAnnual))} ${unit}`;

  return (
    <div className="leading-tight">
      {/* Project annual total — prominent */}
      <span className={`font-medium ${defaultColor}`}>
        {isBurden ? "\u25BC " : "\u25B2 "}
        {formattedValue}
      </span>

      {/* Per-MWh rate in scientific notation */}
      {projectPerMwh != null && (
        <div className="text-[10px] text-text-muted mt-0.5">
          {formatScientific(Math.abs(projectPerMwh))} /MWh
        </div>
      )}

      {/* Grid baseline context */}
      {gridAnnual != null && (
        <div className="text-[10px] text-red-400/60 mt-0.5">
          Grid: {metric === "usd"
            ? formatImpact(Math.abs(gridAnnual), "usd")
            : `${formatCompactValue(Math.abs(gridAnnual))} ${unit}`}
          {gridPerMwh != null && (
            <span className="text-text-muted"> ({formatScientific(Math.abs(gridPerMwh))})</span>
          )}
        </div>
      )}
    </div>
  );
};
